import React, { useEffect, useState } from 'react';
import {
  ArrowLeft, Loader2, Trophy, Check, ChevronRight, Award, Zap,
  Activity, Heart, Footprints, Sparkles, Flame, Play, ChevronDown, ChevronUp,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { youtubeVideoUrl } from '../../lib/exerciseVideos';

/**
 * Mastery hub — premium ladder UI for the five masterclasses.
 * Top level: 5 discipline cards.
 * Tap one: full level ladder with current level highlighted + coaching detail
 *           + "Mark session done" + "Ready to promote" controls.
 */

const DISCIPLINE_META = {
  longevity: {
    label: 'Longevity & Strength',
    sub:   'Over 40s · Lift + Explosive · 4 days max',
    icon: Flame, accent: 'from-orange-400 to-red-500',
    art: '/mastery/ki.png',
    seal: '氣', // Ki — life-force, breath, enduring vitality
    multiTrack: false,
  },
  calisthenics: {
    label: 'Calisthenics Mastery',
    sub:   'Planche · Lever · Pistol · Handstand',
    icon: Trophy, accent: 'from-amber-400 to-orange-500',
    art: '/mastery/go.png',
    seal: '剛', // Gō — hardened, tempered strength
    multiTrack: true,
  },
  pilates_mat: {
    label: 'Pilates Mat',
    sub:   'Classical 34 — foundation → control',
    icon: Sparkles, accent: 'from-rose-400 to-pink-500',
    art: '/mastery/zhong.png',
    seal: '中', // Zhōng — centre, balance
    multiTrack: false,
  },
  reformer: {
    label: 'Reformer Pilates',
    sub:   'Footwork → Box → Long Stretch → Control',
    icon: Sparkles, accent: 'from-fuchsia-400 to-purple-500',
    art: '/mastery/zhong.png',
    seal: '中',
    multiTrack: false,
  },
  circuit: {
    label: 'Circuit Conditioning',
    sub:   'AMRAP · EMOM · Chipper benchmarks',
    icon: Activity, accent: 'from-red-500 to-rose-600',
    art: '/mastery/hi.png',
    seal: '火', // Hi — fire, inner flame
    multiTrack: false,
  },
  running: {
    label: 'Running Fitness',
    sub:   'Walk-jog → 5k → 10k → marathon → ultra',
    icon: Footprints, accent: 'from-emerald-400 to-teal-500',
    art: '/mastery/do.png',
    seal: '道', // Dō — the Way, the road
    multiTrack: false,
  },
};

// ----- Hub (card grid) -----
const Hub = ({ onPick }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      <Trophy className="w-4 h-4 text-[#BEFF38]" />
      <h2 className="text-white font-black uppercase tracking-tight text-lg">Mastery</h2>
    </div>
    <p className="text-zinc-400 text-xs leading-relaxed">
      Five disciplines. Each one is a ladder of named levels with concrete unlock criteria,
      form cues, common faults, and a regression option. Pick one to start your path.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {Object.entries(DISCIPLINE_META).map(([id, m]) => {
        const Icon = m.icon;
        return (
          <button
            key={id}
            onClick={() => onPick(id)}
            data-testid={`mastery-card-${id}`}
            className="text-left rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 hover:border-[#BEFF38]/50 transition-colors group relative overflow-hidden"
          >
            {m.art && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-4 -bottom-4 w-24 h-24 opacity-[0.11] group-hover:opacity-[0.18] transition-opacity"
                style={{ backgroundImage: `url(${m.art})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
              />
            )}
            <div className="relative">
              <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br ${m.accent} mb-3`}>
                <Icon className="w-4 h-4 text-black" strokeWidth={2.5} />
              </div>
              <p className="text-white font-black uppercase tracking-tight text-sm leading-tight">
                {m.label}
                {m.seal && <span className="ml-2 text-zinc-500 font-normal">· {m.seal}</span>}
              </p>
              <p className="text-zinc-400 text-[11px] mt-1 leading-snug">{m.sub}</p>
              <div className="mt-3 flex items-center text-[10px] font-bold uppercase tracking-wider text-[#BEFF38] group-hover:translate-x-0.5 transition-transform">
                Open ladder <ChevronRight className="w-3 h-3 ml-0.5" />
              </div>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

// ----- Single-discipline ladder -----
const Ladder = ({ disciplineId, onBack, refreshHome }) => {
  const meta = DISCIPLINE_META[disciplineId];
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);          // { levels, current, level_idx, total, sessions_at_level }
  const [tracks, setTracks] = useState(null);      // multi-track (calisthenics) only
  const [activeTrack, setActiveTrack] = useState('push');
  const [busy, setBusy] = useState(false);
  const [assessOpen, setAssessOpen] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      if (disciplineId === 'calisthenics') {
        const [cur, prog] = await Promise.all([
          api.calisthenicsCurriculum(), api.calisthenicsProgress(),
        ]);
        setTracks({ curriculum: cur.tracks, progress: prog.tracks });
      } else if (disciplineId === 'pilates_mat' || disciplineId === 'reformer') {
        const prog = await api.pilatesProgress(disciplineId);
        const cur = await api.pilatesCurriculum();
        const disc = cur.disciplines[disciplineId];
        setData({
          levels: disc.levels,
          story: disc.story,
          level_idx: prog.level_idx,
          total: prog.total_levels,
          sessions_at_level: prog.sessions_at_level,
          tick: () => api.pilatesTick(disciplineId),
          promote: () => api.pilatesPromote(disciplineId),
          setLevel: (idx) => api.pilatesSetLevel(disciplineId, idx),
        });
      } else if (disciplineId === 'circuit') {
        const [cur, prog] = await Promise.all([api.circuitsCurriculum(), api.circuitsProgress()]);
        setData({
          levels: cur.levels,
          story: 'AMRAP · EMOM · For-Time · Chipper progression.',
          level_idx: prog.level_idx,
          total: prog.total_levels,
          sessions_at_level: prog.sessions_at_level,
          tick: api.circuitsTick,
          promote: api.circuitsPromote,
          setLevel: api.circuitsSetLevel,
        });
      } else if (disciplineId === 'longevity') {
        const [cur, prog] = await Promise.all([api.longevityCurriculum(), api.longevityProgress()]);
        setData({
          levels: cur.levels,
          story: 'Over-40s strength + explosive training. Dumbbell-first, max 4 days/week, lifting always central.',
          level_idx: prog.level_idx,
          total: prog.total_levels,
          sessions_at_level: prog.sessions_at_level,
          tick: api.longevityTick,
          promote: api.longevityPromote,
          setLevel: api.longevitySetLevel,
        });
      } else if (disciplineId === 'running') {
        const [cur, prog] = await Promise.all([api.runningCurriculum(), api.runningProgress()]);
        setData({
          levels: cur.levels,
          story: 'Couch → ultra. Easy / Tempo / Intervals / Long woven across weeks.',
          level_idx: prog.level_idx,
          total: prog.total_levels,
          sessions_at_level: prog.sessions_at_level,
          tick: api.runningTick,
          promote: api.runningPromote,
          setLevel: api.runningSetLevel,
        });
      }
    } catch (e) {
      toast({ title: 'Could not load ladder', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [disciplineId]);

  const onTick = async (trackId) => {
    setBusy(true);
    try {
      const r = trackId
        ? await api.calisthenicsTick(trackId)
        : await data.tick();
      toast({ title: 'Session marked', description: `${r.sessions_at_level} of 3 at this level.` });
      fetch();
      refreshHome && refreshHome();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const onPromote = async (trackId) => {
    setBusy(true);
    try {
      const r = trackId
        ? await api.calisthenicsPromote(trackId)
        : await data.promote();
      toast({ title: 'Promoted', description: r.new_level?.name || 'New level unlocked.' });
      fetch();
      refreshHome && refreshHome();
    } catch (e) {
      toast({ title: 'Promotion blocked', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const onSetLevel = async (trackId, levelIdx, levelName) => {
    setBusy(true);
    try {
      if (trackId) {
        await api.calisthenicsSetLevel(trackId, levelIdx);
      } else if (data?.setLevel) {
        await data.setLevel(levelIdx);
      }
      toast({ title: 'Jumped to level', description: levelName || `Level ${levelIdx + 1} is now current.` });
      fetch();
      refreshHome && refreshHome();
    } catch (e) {
      toast({ title: 'Could not jump', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="py-8 flex items-center gap-2 text-zinc-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading ladder…
      </div>
    );
  }

  // Multi-track view (Calisthenics)
  if (disciplineId === 'calisthenics' && tracks) {
    const trackIds = Object.keys(tracks.curriculum);
    const tCurr = tracks.curriculum[activeTrack];
    const tProg = tracks.progress[activeTrack];
    return (
      <div className="space-y-4">
        <button onClick={onBack} data-testid="mastery-back" className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-zinc-400 hover:text-[#BEFF38]">
          <ArrowLeft className="w-3.5 h-3.5" /> All masterclasses
        </button>
        {meta.art && (
          <div
            data-testid={`mastery-hero-${disciplineId}`}
            className="rounded-2xl overflow-hidden border border-white/5 bg-[#f7f4ee]"
          >
            <img src={meta.art} alt={`${meta.label} · ${meta.seal || ''}`} className="w-full h-auto block" loading="lazy" />
          </div>
        )}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <h2 className="text-white font-black uppercase tracking-tight text-xl">{meta.label}</h2>
            <p className="text-zinc-400 text-xs mt-0.5">{tCurr.story}</p>
          </div>
          <button
            onClick={() => setAssessOpen(true)}
            data-testid="open-assessment-btn"
            className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#BEFF38] text-black text-[11px] font-black uppercase tracking-wider hover:bg-lime-300"
          >
            <Sparkles className="w-3.5 h-3.5" /> Recommend my levels
          </button>
        </div>
        {/* Track chips */}
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {trackIds.map((tid) => {
            const t = tracks.curriculum[tid];
            const p = tracks.progress[tid];
            const isActive = activeTrack === tid;
            return (
              <button
                key={tid}
                onClick={() => setActiveTrack(tid)}
                data-testid={`mastery-track-${tid}`}
                className={`shrink-0 px-3 py-1.5 rounded-full border text-[10px] font-black uppercase tracking-wider transition-colors ${
                  isActive ? 'bg-[#BEFF38] text-black border-[#BEFF38]' : 'bg-zinc-900/60 text-zinc-300 border-zinc-800'
                }`}
              >
                {t.label} <span className="opacity-60">{p.level_idx + 1}/{p.total_levels}</span>
              </button>
            );
          })}
        </div>
        <LevelList
          levels={tCurr.levels}
          currentIdx={tProg.level_idx}
          sessionsAtLevel={tProg.sessions_at_level}
          onTick={() => onTick(activeTrack)}
          onPromote={() => onPromote(activeTrack)}
          onSetLevel={(idx, name) => onSetLevel(activeTrack, idx, name)}
          busy={busy}
        />
        {assessOpen && (
          <AssessmentModal
            onClose={() => setAssessOpen(false)}
            onApplied={() => { setAssessOpen(false); fetch(); refreshHome && refreshHome(); }}
            loadAssessment={api.calisthenicsAssessment}
            submitAssessment={(a, apply) => api.calisthenicsAssess(a, apply)}
          />
        )}
      </div>
    );
  }

  // Single-ladder view (Pilates / Reformer / Circuit / Running / Longevity)
  return (
    <div className="space-y-4">
      <button onClick={onBack} data-testid="mastery-back" className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-zinc-400 hover:text-[#BEFF38]">
        <ArrowLeft className="w-3.5 h-3.5" /> All masterclasses
      </button>
      {meta.art && (
        <div
          data-testid={`mastery-hero-${disciplineId}`}
          className="rounded-2xl overflow-hidden border border-white/5 bg-[#f7f4ee]"
        >
          <img src={meta.art} alt={`${meta.label} · ${meta.seal || ''}`} className="w-full h-auto block" loading="lazy" />
        </div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h2 className="text-white font-black uppercase tracking-tight text-xl">{meta.label}</h2>
          <p className="text-zinc-400 text-xs mt-0.5">{data.story}</p>
          <p className="text-[10px] uppercase tracking-wider text-[#BEFF38] font-bold mt-1">
            Level {data.level_idx + 1} of {data.total}
          </p>
        </div>
        {['longevity', 'circuit', 'running', 'pilates_mat', 'reformer'].includes(disciplineId) && (
          <button
            onClick={() => setAssessOpen(true)}
            data-testid="open-assessment-btn"
            className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#BEFF38] text-black text-[11px] font-black uppercase tracking-wider hover:bg-lime-300"
          >
            <Sparkles className="w-3.5 h-3.5" /> Recommend my level
          </button>
        )}
      </div>
      <LevelList
        levels={data.levels}
        currentIdx={data.level_idx}
        sessionsAtLevel={data.sessions_at_level}
        onTick={() => onTick(null)}
        onPromote={() => onPromote(null)}
        onSetLevel={(idx, name) => onSetLevel(null, idx, name)}
        busy={busy}
      />
      {assessOpen && (
        <AssessmentModal
          onClose={() => setAssessOpen(false)}
          onApplied={() => { setAssessOpen(false); fetch(); refreshHome && refreshHome(); }}
          loadAssessment={
            disciplineId === 'longevity' ? api.longevityAssessment :
            disciplineId === 'circuit'   ? api.circuitsAssessment  :
            disciplineId === 'running'   ? api.runningAssessment   :
            api.pilatesAssessment
          }
          submitAssessment={
            disciplineId === 'longevity' ? ((a, apply) => api.longevityAssess(a, apply)) :
            disciplineId === 'circuit'   ? ((a, apply) => api.circuitsAssess(a, apply))  :
            disciplineId === 'running'   ? ((a, apply) => api.runningAssess(a, apply))   :
            ((a, apply) => api.pilatesAssess(a, disciplineId, apply))
          }
        />
      )}
    </div>
  );
};

// ----- Collapsible YouTube demo chips -----
const VideoChips = ({ vids }) => {
  const dense = vids.length >= 6;
  const [open, setOpen] = useState(!dense);
  return (
    <div className="mt-2 rounded-lg border border-rose-500/30 bg-rose-500/5 p-2.5" data-testid="video-chips-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="video-chips-toggle"
        className="w-full flex items-center justify-between gap-2 text-left"
        aria-expanded={open}
      >
        <p className="text-rose-400 font-black uppercase tracking-wider text-[10px]">
          Form demos · YouTube <span className="text-rose-300/70">({vids.length})</span>
        </p>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-rose-400" /> : <ChevronDown className="w-3.5 h-3.5 text-rose-400" />}
      </button>
      {open && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {vids.map((name, vi) => (
            <a
              key={vi}
              href={youtubeVideoUrl(name)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`video-link-${vi}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-900/70 border border-zinc-800 text-[10.5px] font-bold text-zinc-200 hover:text-rose-300 hover:border-rose-500/40 transition-colors"
            >
              <Play className="w-3 h-3 text-rose-400" /> {name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// ----- Shared level list -----
const LevelList = ({ levels, currentIdx, sessionsAtLevel, onTick, onPromote, onSetLevel, busy }) => {
  const promoteReady = sessionsAtLevel >= 3;
  const [expandedIdx, setExpandedIdx] = useState(null);
  return (
    <ol className="space-y-2">
      {levels.map((lvl, i) => {
        const state = i < currentIdx ? 'done' : i === currentIdx ? 'current' : 'locked';
        const isOpen = state === 'current' || expandedIdx === i;
        return (
          <li
            key={lvl.id}
            data-testid={`mastery-level-${i}`}
            className={`rounded-xl border p-3 ${
              state === 'current' ? 'border-[#BEFF38] bg-[#BEFF38]/5' :
              state === 'done'    ? 'border-emerald-700/40 bg-emerald-900/10' :
              'border-zinc-800 bg-zinc-900/30'
            }`}
          >
            <div className="flex items-start gap-2">
              <span className={`shrink-0 w-7 h-7 rounded-full grid place-items-center text-[11px] font-black ${
                state === 'current' ? 'bg-[#BEFF38] text-black' :
                state === 'done'    ? 'bg-emerald-500/20 text-emerald-400' :
                'bg-zinc-800 text-zinc-500'
              }`}>
                {state === 'done' ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => state !== 'current' && setExpandedIdx(expandedIdx === i ? null : i)}
                  data-testid={`mastery-level-toggle-${i}`}
                  className="w-full text-left flex items-baseline justify-between gap-2 flex-wrap"
                  aria-expanded={isOpen}
                >
                  <p className={`font-bold text-sm ${state === 'current' ? 'text-white' : state === 'done' ? 'text-emerald-300' : 'text-zinc-400'}`}>
                    {lvl.name}
                  </p>
                  <p className={`text-[10px] font-bold uppercase tracking-wider ${state === 'current' ? 'text-[#BEFF38]' : 'text-zinc-500'}`}>
                    {lvl.target || lvl.session || lvl.block}
                  </p>
                </button>

                {/* Full detail on current level OR when user has tapped to expand */}
                {isOpen && (
                  <div className="mt-2 space-y-1.5 text-[11px] text-zinc-300">
                    {lvl.movements && (
                      <p><span className="text-[#BEFF38] font-black uppercase tracking-wider text-[10px]">Movements:</span> {lvl.movements.join(' · ')}</p>
                    )}
                    {lvl.format && (
                      <p><span className="text-[#BEFF38] font-black uppercase tracking-wider text-[10px]">Format:</span> {lvl.format}</p>
                    )}
                    {lvl.pace_zone && (
                      <p><span className="text-[#BEFF38] font-black uppercase tracking-wider text-[10px]">Pace zone:</span> {lvl.pace_zone}</p>
                    )}
                    {lvl.form_cues && (
                      <p><span className="text-[#BEFF38] font-black uppercase tracking-wider text-[10px]">Cues:</span> {lvl.form_cues.join(' · ')}</p>
                    )}
                    {lvl.faults && (
                      <p><span className="text-rose-400 font-black uppercase tracking-wider text-[10px]">Watch:</span> {lvl.faults.join(' · ')}</p>
                    )}
                    {lvl.regression && (
                      <p><span className="text-amber-400 font-black uppercase tracking-wider text-[10px]">Regression:</span> {lvl.regression}</p>
                    )}
                    {lvl.scaling && (
                      <p><span className="text-amber-400 font-black uppercase tracking-wider text-[10px]">Scaling:</span> {lvl.scaling}</p>
                    )}
                    {lvl.strategy && (
                      <p><span className="text-sky-400 font-black uppercase tracking-wider text-[10px]">Strategy:</span> {lvl.strategy}</p>
                    )}
                    {lvl.tempo && lvl.tempo !== 'isometric' && (
                      <p><span className="text-zinc-500 font-black uppercase tracking-wider text-[10px]">Tempo:</span> {lvl.tempo}</p>
                    )}
                    {lvl.breath && (
                      <p><span className="text-zinc-500 font-black uppercase tracking-wider text-[10px]">Breath:</span> {lvl.breath}</p>
                    )}
                    {lvl.springs && (
                      <p><span className="text-zinc-500 font-black uppercase tracking-wider text-[10px]">Springs:</span> {lvl.springs}</p>
                    )}
                    {lvl.week_view && lvl.week_view.length > 0 && (
                      <div className="mt-2 rounded-lg border border-orange-500/30 bg-orange-500/5 p-2.5">
                        <p className="text-orange-400 font-black uppercase tracking-wider text-[10px] mb-1.5">Sample week</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                          {lvl.week_view.map((d, di) => (
                            <div key={di} className="rounded-md bg-zinc-900/70 border border-zinc-800 p-2" data-testid={`week-day-${di}`}>
                              <p className="text-[10px] font-black uppercase tracking-wider text-orange-400">{d.day}</p>
                              <p className="text-[10.5px] font-bold text-white mt-0.5 leading-tight">{d.focus}</p>
                              <p className="text-[10px] text-zinc-400 mt-1 leading-snug">{d.session}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(() => {
                      let vids = [];
                      if (Array.isArray(lvl.videos) && lvl.videos.length) {
                        vids = lvl.videos;
                      } else if (Array.isArray(lvl.movements) && lvl.movements.length) {
                        vids = lvl.movements;
                      } else if (typeof lvl.block === 'string' && lvl.block.includes('·')) {
                        vids = lvl.block
                          .split('·')
                          .map((s) => s.replace(/^\s*(?:Min\s*\d+\s*:|[A-Z]\d?\s*:?|\d+(?:\.\d+)?\s*(?:s|sec|min|kg|m|cm|cal|x))?\s*/i, '').trim())
                          .map((s) => s.replace(/^\d+\s+/, '').trim())
                          .filter(Boolean);
                      } else if (lvl.name) {
                        vids = [lvl.name];
                      }
                      if (vids.length === 0) return null;
                      return <VideoChips vids={vids} />;
                    })()}
                    <p className="text-zinc-500"><span className="text-[#BEFF38] font-black uppercase tracking-wider text-[10px]">Unlock:</span> {lvl.unlock}</p>

                    {/* Action row: visible only on the actual current level */}
                    {state === 'current' && (
                    <div className="mt-3 rounded-lg bg-zinc-900/60 border border-zinc-800 p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-black text-zinc-400">Sessions at this level</p>
                          <div className="flex items-center gap-1 mt-1">
                            {[0, 1, 2].map((idx) => (
                              <span
                                key={idx}
                                data-testid={`mastery-session-dot-${idx}`}
                                className={`h-2 w-8 rounded-full ${idx < sessionsAtLevel ? 'bg-[#BEFF38]' : 'bg-zinc-700'}`}
                              />
                            ))}
                            <span className="ml-2 text-xs text-white font-bold">{sessionsAtLevel} / 3</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          onClick={onTick}
                          disabled={busy}
                          data-testid="mastery-mark-done"
                          className="flex-1 h-9 rounded-full bg-[#BEFF38] text-black text-[11px] font-black uppercase tracking-wider hover:bg-[#a6e62e] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                          Mark session done
                        </button>
                        <button
                          onClick={onPromote}
                          disabled={busy || !promoteReady}
                          data-testid="mastery-promote"
                          className={`flex-1 h-9 rounded-full text-[11px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5 ${
                            promoteReady
                              ? 'bg-[#ff7a1a] text-black hover:bg-[#ff8a33]'
                              : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                          }`}
                        >
                          <Award className="w-3.5 h-3.5" /> {promoteReady ? 'Promote' : 'Promote (3 needed)'}
                        </button>
                      </div>
                    </div>
                    )}

                    {/* Override: shown on done/locked levels — lets user jump to any level */}
                    {state !== 'current' && onSetLevel && (
                      <button
                        onClick={() => onSetLevel(i, lvl.name)}
                        disabled={busy}
                        data-testid={`mastery-set-current-${i}`}
                        className="mt-3 w-full h-9 rounded-full bg-[#ff7a1a] text-black text-[11px] font-black uppercase tracking-wider hover:bg-[#ff8a33] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                        Jump to this level
                      </button>
                    )}
                  </div>
                )}

                {/* One-line state summary when collapsed */}
                {!isOpen && state === 'done' && (
                  <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-400 mt-1">Cleared · Tap to view</p>
                )}
                {!isOpen && state === 'locked' && (
                  <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-600 mt-1">Tap to preview · {lvl.unlock}</p>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

// ----- Form-aware self-assessment modal (works for any discipline) -----
const AssessmentModal = ({ onClose, onApplied, loadAssessment, submitAssessment }) => {
  const [questions, setQuestions] = useState([]);
  const [maxScore, setMaxScore] = useState(10);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    let alive = true;
    loadAssessment().then((d) => {
      if (!alive) return;
      setQuestions(d.questions || []);
      setMaxScore(d.max_score || 10);
    });
    return () => { alive = false; };
  }, [loadAssessment]);

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id]);

  const submit = async (apply) => {
    setBusy(true);
    try {
      const r = await submitAssessment(answers, apply);
      setResult(r);
      if (apply) {
        const label = r.recommended_level?.name
          || (r.recommended_per_track ? 'Per-track levels set' : 'Level set');
        toast({ title: 'Level set', description: label });
        onApplied();
      }
    } catch (e) {
      toast({ title: 'Could not score', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[10010] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
      onClick={onClose}
      data-testid="assessment-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-6 text-white max-h-[88vh] overflow-y-auto"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[#BEFF38] text-[11px] uppercase tracking-wider font-black">Recommend my level</p>
            <h3 className="mt-1 text-xl font-bold">Form-honest self-check</h3>
            <p className="text-sm text-zinc-400 mt-1">5 movements. Answer honestly using the form standard — watch the demo first if you&apos;re unsure.</p>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-zinc-800 grid place-items-center hover:bg-zinc-700" data-testid="assessment-close-btn" aria-label="Close">
            <span className="text-zinc-300 text-lg leading-none">×</span>
          </button>
        </div>

        {!result && (
          <>
            <div className="mt-5 space-y-3">
              {questions.map((q) => (
                <div key={q.id} className="rounded-lg bg-zinc-950/70 border border-zinc-800 p-3" data-testid={`assess-q-${q.id}`}>
                  <p className="text-sm font-bold text-white">{q.prompt}</p>
                  <p className="text-[11px] text-zinc-400 mt-1 leading-snug">{q.form}</p>
                  <a
                    href={youtubeVideoUrl(q.video)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`assess-q-${q.id}-demo`}
                    className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-zinc-900 border border-zinc-800 text-[10.5px] font-bold text-rose-300 hover:border-rose-500/40"
                  >
                    <Play className="w-3 h-3 text-rose-400" /> Watch form first
                  </a>
                  <div className="mt-2.5 grid grid-cols-3 gap-1.5">
                    {[
                      { v: 'yes', label: 'Yes — clean form' },
                      { v: 'almost', label: 'Almost' },
                      { v: 'no', label: 'Not yet' },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.v }))}
                        data-testid={`assess-q-${q.id}-${opt.v}`}
                        className={`h-9 rounded-md text-[10.5px] font-bold uppercase tracking-wider transition-colors ${
                          answers[q.id] === opt.v
                            ? (opt.v === 'yes' ? 'bg-[#BEFF38] text-black' : opt.v === 'almost' ? 'bg-amber-400 text-black' : 'bg-rose-500 text-white')
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => submit(false)}
              disabled={!allAnswered || busy}
              data-testid="assess-score-btn"
              className="mt-5 w-full h-11 rounded-full bg-[#ff7a1a] text-black font-bold flex items-center justify-center gap-2 hover:bg-[#ff8a33] disabled:opacity-50 transition-colors"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              See my recommended level
            </button>
            <p className="mt-2 text-[10.5px] text-zinc-500 text-center">
              {questions.length > 0 ? `${Object.keys(answers).length} / ${questions.length} answered` : 'Loading…'}
            </p>
          </>
        )}

        {result && (
          <div className="mt-5">
            <p className="text-[11px] uppercase tracking-wider font-black text-[#BEFF38]">Score</p>
            <p className="text-3xl font-extrabold text-white">{result.score} <span className="text-zinc-500 text-base font-bold">/ {result.max_score}</span></p>

            {result.recommended_level && (
              <>
                <p className="mt-4 text-[11px] uppercase tracking-wider font-black text-[#ff7a1a]">We recommend starting at</p>
                <p className="text-lg font-bold text-white">Level {result.recommended_idx + 1} · {result.recommended_level?.name}</p>
                <p className="text-xs text-zinc-400 mt-1">{result.recommended_level?.target}</p>
              </>
            )}

            {result.recommended_per_track && (
              <>
                <p className="mt-4 text-[11px] uppercase tracking-wider font-black text-[#ff7a1a]">Recommended starting levels</p>
                <ul className="mt-1 space-y-1">
                  {Object.entries(result.recommended_per_track).map(([tid, info]) => (
                    <li key={tid} className="flex items-center justify-between text-xs">
                      <span className="text-zinc-400 capitalize">{tid}</span>
                      <span className="text-white font-bold">Lv {info.level_idx + 1} · {info.level}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="mt-5 flex items-center gap-2">
              <button
                onClick={() => submit(true)}
                disabled={busy}
                data-testid="assess-apply-btn"
                className="flex-1 h-11 rounded-full bg-[#BEFF38] text-black font-bold flex items-center justify-center gap-2 hover:bg-lime-300 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" strokeWidth={3} />}
                Set as my level
              </button>
              <button
                onClick={() => { setResult(null); setAnswers({}); }}
                data-testid="assess-retake-btn"
                className="h-11 px-4 rounded-full bg-zinc-800 text-zinc-300 font-bold hover:bg-zinc-700"
              >
                Retake
              </button>
            </div>
            <p className="mt-3 text-[10.5px] text-zinc-500 text-center">
              You can always jump to a different level from the ladder.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Mastery = ({ refreshHome }) => {
  const [active, setActive] = useState(null);  // null = hub, else disciplineId
  return active
    ? <Ladder disciplineId={active} onBack={() => setActive(null)} refreshHome={refreshHome} />
    : <Hub onPick={setActive} />;
};

export default Mastery;
